// Phase 78aj — Route Preview Modal.
//
// Renders the polyline + numbered markers returned by the
// /api/trips/{id}/extra-stops/optimize endpoint (dry_run mode) so the
// booker/admin can visually confirm the suggested order before
// applying it.
//
// Props:
//   tripId        – string
//   preview       – the JSON response payload from optimize (with dry_run=true)
//   onClose       – () => void  (close without applying)
//   onApplied     – () => void  (called after the second POST applies the order)
//
import React, { useMemo } from 'react';
import { GoogleMap, Polyline } from '@react-google-maps/api';
import { useGoogleMaps, GOOGLE_MAPS_MAP_ID } from '../lib/googleMaps';
import AdvancedMarker from './maps/AdvancedMarker';
import api from '../lib/api';

function decodePath(encoded) {
  // Phase 78aj — Decode Google's encoded polyline using the geometry
  // library if available, else fall back to a tiny pure-JS decoder.
  if (window.google?.maps?.geometry?.encoding?.decodePath) {
    try {
      const pts = window.google.maps.geometry.encoding.decodePath(encoded);
      return pts.map(p => ({ lat: p.lat(), lng: p.lng() }));
    } catch { /* fall through */ }
  }
  // Pure JS fallback (Mapbox polyline algorithm — matches Google).
  const points = [];
  let idx = 0, lat = 0, lng = 0;
  while (idx < encoded.length) {
    let result = 0, shift = 0, b;
    do { b = encoded.charCodeAt(idx++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    const dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lat += dlat;
    result = 0; shift = 0;
    do { b = encoded.charCodeAt(idx++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    const dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lng += dlng;
    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return points;
}

function MapLeg({ side, color, polyline, anchor, stops }) {
  const path = useMemo(() => (polyline ? decodePath(polyline) : []), [polyline]);
  const center = path.length ? path[Math.floor(path.length / 2)] : anchor;
  if (!center) return null;
  return (
    <div className="border border-[#E5E5E5] rounded-xl overflow-hidden" data-testid={`route-preview-map-${side}`}>
      <div className="bg-[#F8F8F8] px-2 py-1.5 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color }}>
          {side === 'pickup' ? 'Pickup leg' : 'Drop leg'} — {stops.length} stops
        </span>
      </div>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '220px' }}
        center={center}
        zoom={9}
        options={{ mapId: GOOGLE_MAPS_MAP_ID, disableDefaultUI: true, zoomControl: true }}
      >
        {path.length > 0 && (
          <Polyline
            path={path}
            options={{ strokeColor: color, strokeOpacity: 0.85, strokeWeight: 4 }}
          />
        )}
        {/* Origin pin (anchor = main pickup / drop) */}
        {anchor && (
          <AdvancedMarker position={anchor} title="Start">
            <div className="bg-[#0A0A0A] text-white text-[10px] font-bold rounded-full px-2 py-0.5">A</div>
          </AdvancedMarker>
        )}
        {/* Numbered markers for each stop in the new order. */}
        {stops.map((s, i) => (
          s.lat && s.lng ? (
            <AdvancedMarker key={s.id} position={{ lat: s.lat, lng: s.lng }} title={s.supplier_name}>
              <div
                className="text-white text-[10px] font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-md"
                style={{ background: color, opacity: s.reached_at ? 0.5 : 1 }}
              >{i + 2}</div>
            </AdvancedMarker>
          ) : null
        ))}
      </GoogleMap>
    </div>
  );
}

export default function RoutePreviewModal({ tripId, preview, onClose, onApplied }) {
  const { isLoaded } = useGoogleMaps();
  const main = preview?.main || {};
  const pickupAnchor = main.pickup_lat && main.pickup_lng ? { lat: main.pickup_lat, lng: main.pickup_lng } : null;
  const dropAnchor = main.drop_lat && main.drop_lng ? { lat: main.drop_lat, lng: main.drop_lng } : null;

  const applyOrder = async () => {
    try {
      await api.post(`/trips/${tripId}/extra-stops/optimize`, { dry_run: false });
      if (onApplied) onApplied();
    } catch (err) {
      alert(err.response?.data?.detail || 'Apply failed');
    }
  };

  const renderSide = (side, color) => {
    const r = preview?.[side];
    if (!r) return null;
    if (!r.changed) {
      return (
        <div className="rounded-xl border border-[#E5E5E5] bg-[#F8F8F8] p-3 text-[11px] text-[#666]" data-testid={`route-preview-${side}-skip`}>
          <span className="font-bold uppercase tracking-widest" style={{ color }}>{side}</span>{' '}
          {r.reason || 'no change suggested'}
        </div>
      );
    }
    const anchor = side === 'pickup' ? pickupAnchor : pickupAnchor; // both legs start at main pickup per backend logic
    return (
      <div className="space-y-2">
        <MapLeg side={side} color={color} polyline={r.polyline} anchor={anchor} stops={r.stops || []} />
        <div className="text-[11px] text-[#0A0A0A]">
          <p className="font-bold uppercase tracking-widest" style={{ color }}>{side} leg · ~{r.duration_min}m · {r.distance_km} km</p>
          <ol className="list-decimal list-inside text-[#666]">
            {(r.stops || []).map((s, i) => (
              <li key={s.id} className={s.reached_at ? 'opacity-60' : ''}>
                {s.supplier_name}{s.reached_at ? ' (reached)' : ''}
              </li>
            ))}
          </ol>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5 space-y-3 rounded-2xl" data-testid="route-preview-modal">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-bold">Suggested Route Preview</h2>
          <button onClick={onClose} className="text-[#666] hover:text-[#0A0A0A] text-2xl leading-none" data-testid="route-preview-close">×</button>
        </div>
        <p className="text-xs text-[#666]">
          Powered by Google Maps. {preview?.applied ? 'Already applied.' : 'Confirm to apply the new order, or close to keep things as they are.'}
        </p>
        {!isLoaded ? (
          <div className="h-[220px] bg-[#F3F3F3] flex items-center justify-center text-[10px] uppercase tracking-widest text-[#666]">Loading map…</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {renderSide('pickup', '#00C853')}
            {renderSide('drop', '#FF3333')}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 pt-2">
          <button
            onClick={onClose}
            className="py-2 text-sm font-bold uppercase tracking-widest border border-[#E5E5E5] hover:bg-[#F3F3F3] rounded-full"
            data-testid="route-preview-cancel"
          >Cancel</button>
          <button
            onClick={applyOrder}
            disabled={!preview?.pickup?.changed && !preview?.drop?.changed}
            className="py-2 text-sm font-bold uppercase tracking-widest bg-[#7C3AED] text-white hover:bg-[#6D28D9] disabled:bg-[#E5E5E5] disabled:text-[#999] disabled:cursor-not-allowed rounded-full"
            data-testid="route-preview-apply"
          >Apply this order</button>
        </div>
      </div>
    </div>
  );
}
